import isPast from 'date-fns/isPast';
import { router } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import {
    type ImageSourcePropType,
    Pressable,
    StyleSheet,
    TouchableWithoutFeedback,
} from 'react-native';
import Carousel, {
    type ICarouselInstance,
} from 'react-native-reanimated-carousel';
import {
    BodySmallRegular,
    ButtonVariant,
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    H2Emphasized,
    H5Emphasized,
    H6Regular,
    Icon,
    IconButton,
    Image,
    isWeb,
    LabelEmphasized,
    Shadows,
    useMedia,
    useWindowDimensions,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useMembershipGuidanceSessionInfo } from '../hooks/useMembershipGuidanceSessionInfo';
import type {
    GuidanceSession,
    GuidanceSessionType,
} from '../types/GuidanceSessions';

const CARD_HEADER_HEIGHT = '38%';

export interface SessionCarouselProps {
    pastSessions: GuidanceSession[];
    upcomingSessions: GuidanceSession[];
    onCompletedCardPress: () => void;
    onIneligibleCardPress: () => void;
    maxWidth: number;
    baseCardWidth: number;
}

export const SessionCarousel = ({
    pastSessions,
    upcomingSessions,
    onCompletedCardPress,
    onIneligibleCardPress,
    maxWidth,
    baseCardWidth,
}: SessionCarouselProps) => {
    const { guidanceOnboardingOrder, getNextGuidanceOnboardingSessionType } =
        useMembershipGuidanceSessionInfo();
    const { width } = useWindowDimensions();
    const breakpoints = useMedia();
    const [allowCardPress, setAllowCardPress] = useState(true);
    const [pressedCardType, setPressedCardType] =
        useState<GuidanceSessionType | null>(null);
    const ref = useRef<ICarouselInstance>(null);
    const multipleCardsShowing = Math.min(width, maxWidth) >= 2 * baseCardWidth;
    const allCardsShowing =
        Math.min(width, maxWidth) >=
        guidanceOnboardingOrder.length * baseCardWidth;

    useEffect(() => {
        if (!allCardsShowing && ref.current !== null) {
            const nextSessionType =
                getNextGuidanceOnboardingSessionType(pastSessions);

            let nextSessionIndex =
                nextSessionType != null
                    ? guidanceOnboardingOrder.indexOf(nextSessionType)
                    : 0;

            if (multipleCardsShowing) {
                // When showing multiple cards, we want to offset the index by 1 so the set of cards showing includes the context of the most recently completed session
                nextSessionIndex -= 1;
            }

            if (
                nextSessionIndex >= 0 &&
                nextSessionIndex < guidanceOnboardingOrder.length
            ) {
                ref.current.scrollTo({
                    index: nextSessionIndex,
                    animated: false,
                });
            }
        }
    }, []);

    useEffect(() => {
        if (!allCardsShowing && pressedCardType !== null) {
            ref.current?.scrollTo({
                index: guidanceOnboardingOrder.indexOf(pressedCardType),
                animated: false,
            });
        }
    }, [pressedCardType]);

    const currentIndex = ref.current?.getCurrentIndex() ?? 0;
    const enablePrevButton = currentIndex > 0;
    const enableNextButton = currentIndex < guidanceOnboardingOrder.length - 1;

    return (
        <View width="100%">
            <Carousel
                ref={ref}
                width={breakpoints.sm ? baseCardWidth : width}
                style={{ width: '100%' }}
                height={440}
                data={guidanceOnboardingOrder}
                loop={false}
                onProgressChange={() => {}}
                onScrollStart={() => {
                    setAllowCardPress(false);
                }}
                onScrollEnd={() => {
                    setAllowCardPress(true);
                }}
                mode="parallax"
                modeConfig={{
                    parallaxScrollingScale: 1,
                    parallaxScrollingOffset: breakpoints.sm ? 0 : 55,
                    parallaxAdjacentItemScale: breakpoints.sm ? 1 : 0.95,
                }}
                renderItem={({ item }: { item: GuidanceSessionType }) => (
                    <SessionCarouselItem
                        item={item}
                        pastSessions={pastSessions}
                        upcomingSessions={upcomingSessions}
                        onCompletedCardPress={() => {
                            setPressedCardType(item);
                            onCompletedCardPress();
                        }}
                        onIneligibleCardPress={() => {
                            setPressedCardType(item);
                            onIneligibleCardPress();
                        }}
                        allowCardPress={allowCardPress}
                    />
                )}
            />
            {isWeb && !allCardsShowing && (
                <View
                    flexDirection="row"
                    justifyContent="space-between"
                    paddingHorizontal={20}
                    $sm={{ paddingHorizontal: 8 }}
                >
                    <View opacity={enablePrevButton ? 1 : 0.3}>
                        <IconButton
                            variant={ButtonVariant.SECONDARY}
                            icon={ChevronLeftIcon}
                            onPress={() => ref.current?.prev()}
                            disabled={!enablePrevButton}
                        />
                    </View>
                    <View opacity={enableNextButton ? 1 : 0.3}>
                        <IconButton
                            variant={ButtonVariant.SECONDARY}
                            icon={ChevronRightIcon}
                            onPress={() => ref.current?.next()}
                            disabled={!enableNextButton}
                        />
                    </View>
                </View>
            )}
        </View>
    );
};

const SessionCarouselItem = ({
    item,
    pastSessions,
    upcomingSessions,
    onCompletedCardPress,
    onIneligibleCardPress,
    allowCardPress,
}: Omit<SessionCarouselProps, 'maxWidth' | 'baseCardWidth'> & {
    item: GuidanceSessionType;
    allowCardPress: boolean;
}) => {
    const {
        guidanceOnboardingOrder,
        getNextGuidanceOnboardingSessionType,
        getSessionDetails,
    } = useMembershipGuidanceSessionInfo();
    const allSessions = [...pastSessions, ...upcomingSessions];
    const nextGuidanceSessionType =
        getNextGuidanceOnboardingSessionType(pastSessions);
    const hasUpcomingSession = upcomingSessions.length > 0;

    const session = allSessions.find((session) => session.type === item);

    const canBook = !hasUpcomingSession && nextGuidanceSessionType === item;

    // Session was skipped if: 1) No session exists, 2) The next roadmap
    // session is a higher index in the sequence than the current item.
    const nextGuidanceSessionIndex =
        nextGuidanceSessionType == null
            ? guidanceOnboardingOrder.length
            : guidanceOnboardingOrder.indexOf(nextGuidanceSessionType);
    const currentItemIndex = guidanceOnboardingOrder.indexOf(item);
    const wasSessionSkipped =
        session == null && nextGuidanceSessionIndex > currentItemIndex;

    const showBookSessionCard = session == null && !wasSessionSkipped;

    const { title, description, image } = getSessionDetails(item);

    return (
        <View
            flexGrow={1}
            maxHeight={420}
            scale={0.9}
            marginHorizontal={16}
            $sm={{ scale: 1 }}
        >
            {showBookSessionCard ? (
                <SessionCard
                    sessionType={item}
                    canBook={canBook}
                    handleIneligibleCardPress={onIneligibleCardPress}
                    allowCardPress={allowCardPress}
                    title={title}
                    description={description}
                    image={image}
                />
            ) : (
                <CompletedSessionCard
                    sessionType={item}
                    session={session}
                    allowCardPress={allowCardPress}
                    onPress={onCompletedCardPress}
                    title={title}
                    image={image}
                />
            )}
        </View>
    );
};

interface BookSessionCardProps {
    sessionType: GuidanceSessionType;
    canBook: boolean;
    handleIneligibleCardPress: () => void;
    allowCardPress: boolean;
    title: string;
    description: string;
    image: ImageSourcePropType;
}

const SessionCard = ({
    sessionType,
    canBook,
    handleIneligibleCardPress,
    allowCardPress,
    title,
    description,
    image,
}: BookSessionCardProps) => {
    const onPress = () => {
        if (!allowCardPress) return;
        if (canBook) {
            router.navigate({
                pathname: '/guidance-sessions/book-session-details',
                params: {
                    sessionType,
                },
            });
        } else {
            handleIneligibleCardPress();
        }
    };

    return (
        <TouchableWithoutFeedback onPress={onPress} style={{ flex: 1 }}>
            <YStack
                backgroundColor="$peach300"
                style={[styles.baseCardStyles, styles.shadowStyle]}
                cursor="pointer"
            >
                <YStack height={CARD_HEADER_HEIGHT} gap={6}>
                    <H5Emphasized>{title}</H5Emphasized>
                    <BodySmallRegular>{description}</BodySmallRegular>
                </YStack>
                <XStack justifyContent="center" alignItems="center">
                    <Image
                        source={image}
                        resizeMode="contain"
                        style={{ height: 174, width: 174 }}
                    />
                </XStack>
            </YStack>
        </TouchableWithoutFeedback>
    );
};

interface CompletedSessionCardProps {
    sessionType: GuidanceSessionType;
    session?: GuidanceSession;
    allowCardPress: boolean;
    onPress: () => void;
    title: string;
    image: ImageSourcePropType;
}

const CompletedSessionCard = ({
    session,
    onPress,
    allowCardPress,
    title,
    image,
}: CompletedSessionCardProps) => {
    const isCompleted =
        session != null ? isPast(new Date(session?.endTime)) : false;
    const scheduledForDate =
        session != null ? getFormattedDateTime(session.startTime) : null;
    const completedOnDate =
        session != null ? getFormattedDateTime(session.endTime) : null;
    const handleCardPress = () => {
        if (!allowCardPress) return;
        onPress();
    };
    return (
        <Pressable
            onPress={handleCardPress}
            style={({ pressed }) => [
                {
                    flex: 1,
                    opacity: pressed ? 0.5 : 1,
                },
            ]}
            delayLongPress={250}
        >
            <YStack
                backgroundColor="$peach300"
                style={[styles.baseCardStyles, styles.shadowStyle]}
                cursor="pointer"
            >
                <YStack height={CARD_HEADER_HEIGHT}>
                    <LabelEmphasized>{title}</LabelEmphasized>
                    {session ? (
                        isCompleted ? (
                            <>
                                <H6Regular>Completed on</H6Regular>
                                <H2Emphasized>{completedOnDate}</H2Emphasized>
                            </>
                        ) : (
                            <>
                                <H6Regular>Scheduled for</H6Regular>
                                <H2Emphasized>{scheduledForDate}</H2Emphasized>
                            </>
                        )
                    ) : (
                        <H2Emphasized>Completed</H2Emphasized>
                    )}
                </YStack>

                <XStack
                    width="100%"
                    justifyContent="center"
                    alignItems="center"
                >
                    <View opacity={0.1}>
                        <Image
                            source={image}
                            resizeMode="contain"
                            style={{ height: 174, width: 174 }}
                        />
                    </View>
                    <View
                        position="absolute"
                        height={40}
                        width={40}
                        borderRadius={50}
                        justifyContent="center"
                        alignItems="center"
                        backgroundColor="rgba(255, 255, 255, 0.50)"
                    >
                        <Icon icon={CheckIcon} color="$green1000" size={28} />
                    </View>
                </XStack>
            </YStack>
        </Pressable>
    );
};

const getFormattedDateTime = (dateTime: string) => {
    const sessionDate = new Date(dateTime);
    const day = sessionDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
    });
    const time = sessionDate
        .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
        })
        .replaceAll(' ', '');

    return `${day}, ${time}`;
};

const styles = StyleSheet.create({
    baseCardStyles: {
        flex: 1,
        padding: 20,
        marginTop: 20,
        borderRadius: 20,
    },
    shadowStyle: Shadows.LARGE,
});
